import { useState } from 'react';
import { trpc } from '../lib/trpc';
import { useTranslation } from '../i18n';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, FileText, Sparkles } from 'lucide-react';

type EditingState = {
  id: number | null;
  slug: string;
  title: string;
  content: Record<string, string>; // language code -> content
  isActive: boolean;
  sortOrder: number;
  lang: string; // currently selected language for editing
};

const SUPPORTED_LANGUAGES = ['hr', 'de', 'en', 'bs'] as const;
type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];

export function LegalPagesManager() {
  const { t, language } = useTranslation();
  const legalText = t.settings.legalTab;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [state, setState] = useState<EditingState>({
    id: null,
    slug: '',
    title: '',
    content: Object.fromEntries(SUPPORTED_LANGUAGES.map((l) => [l, ''])),
    isActive: true,
    sortOrder: 0,
    lang: language
  });

  const { data: pages, refetch } = trpc.legalPages.list.useQuery();

  const createMutation = trpc.legalPages.create.useMutation({
    onSuccess: () => { toast.success(legalText.toastCreated); refetch(); close(); },
    onError: (e) => toast.error(e.message),
  });
  const updateMutation = trpc.legalPages.update.useMutation({
    onSuccess: () => { toast.success(legalText.toastUpdated); refetch(); close(); },
    onError: (e) => toast.error(e.message),
  });
  const deleteMutation = trpc.legalPages.delete.useMutation({
    onSuccess: () => { toast.success(legalText.toastDeleted); refetch(); },
    onError: (e) => toast.error(e.message),
  });

  const openCreate = () => {
    setState({
      id: null,
      slug: '',
      title: '',
      content: Object.fromEntries(SUPPORTED_LANGUAGES.map((l) => [l, ''])),
      isActive: true,
      sortOrder: 0,
      lang: language
    });
    setDialogOpen(true);
  };
  const openEdit = (p: any) => {
    // p.content is expected to be an object { [lang]: content }
    setState({
      id: p.id,
      slug: p.slug,
      title: p.title,
      content: { ...Object.fromEntries(SUPPORTED_LANGUAGES.map((l) => [l, ''])), ...(p.content || {}) },
      isActive: p.isActive,
      sortOrder: p.sortOrder,
      lang: language
    });
    setDialogOpen(true);
  };
  const close = () => setDialogOpen(false);

  const save = () => {
    if (!state.slug || !state.title || Object.values(state.content).every(c => !c)) {
      toast.error(legalText.validationError);
      return;
    }
    const payload = {
      slug: state.slug,
      title: state.title,
      content: state.content, // now an object { [lang]: content }
      isActive: state.isActive,
      sortOrder: state.sortOrder
    };
    if (state.id) updateMutation.mutate({ id: state.id, ...payload }); else createMutation.mutate(payload);
  };
  const remove = (id: number) => { if (confirm(legalText.deleteConfirm)) deleteMutation.mutate({ id }); };

  const ensureDefaults = async () => {
    const defaults = Object.entries(legalText.defaultPages).map(([slug, title]) => ({ slug, title }));
    const existing = new Set((pages||[]).map(p => p.slug));
    const toCreate = defaults.filter(d => !existing.has(d.slug));
    if (toCreate.length === 0) { toast.info(legalText.defaultsAlready); return; }
    try {
      for (const d of toCreate) {
        // Default content for all languages
        const content: Record<string, string> = {};
        for (const l of SUPPORTED_LANGUAGES) {
          content[l] = `<h2>${d.title}</h2><p>${legalText.defaultContentNote}</p>`;
        }
        await createMutation.mutateAsync({
          slug: d.slug,
          title: d.title,
          content,
          isActive: true,
          sortOrder: 0,
        });
      }
      toast.success(legalText.defaultsCreated);
      refetch();
    } catch (e:any) {
      toast.error(e?.message || legalText.defaultsFailed);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className='flex items-center justify-between'>
          <div>
            <CardTitle>{legalText.title}</CardTitle>
            <CardDescription>{legalText.description}</CardDescription>
          </div>
          <div className='flex gap-2'>
            <Button variant='outline' onClick={ensureDefaults}><Sparkles className='w-4 h-4 mr-2'/>{legalText.defaultsButton}</Button>
            <Button onClick={openCreate}><Plus className='w-4 h-4 mr-2'/>{legalText.createButton}</Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className='space-y-2'>
          {(pages||[]).length === 0 && <div className='text-sm text-muted-foreground'>{legalText.emptyState}</div>}
          {(pages||[]).map(p => (
            <div key={p.id} className='flex items-start justify-between border rounded-md p-3'>
              <div className='space-y-1'>
                <div className='flex items-center gap-2'>
                  <FileText className='w-4 h-4'/>
                  <span className='font-medium'>{p.title}</span>
                  {!p.isActive && <span className='text-xs px-2 py-0.5 rounded bg-muted'>{legalText.inactiveBadge}</span>}
                </div>
                <div className='text-xs text-muted-foreground'>/{p.slug}</div>
              </div>
              <div className='flex gap-2'>
                <Button variant='outline' size='sm' onClick={()=>openEdit(p)}><Edit className='w-4 h-4'/></Button>
                <Button variant='destructive' size='sm' onClick={()=>remove(p.id)}><Trash2 className='w-4 h-4'/></Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className='max-w-2xl'>
          <DialogHeader>
            <DialogTitle>{state.id ? legalText.dialog.editTitle : legalText.dialog.createTitle}</DialogTitle>
            <DialogDescription>{legalText.dialog.description}</DialogDescription>
          </DialogHeader>
          <div className='space-y-4'>
            <div>
              <label className='text-sm font-medium'>{legalText.dialog.slugLabel}</label>
              <Input value={state.slug} onChange={e=>setState(s=>({...s, slug: e.target.value.toLowerCase()}))} placeholder={legalText.dialog.slugPlaceholder} />
            </div>
            <div>
              <label className='text-sm font-medium'>{legalText.dialog.titleLabel}</label>
              <Input value={state.title} onChange={e=>setState(s=>({...s, title: e.target.value}))} placeholder={legalText.dialog.titlePlaceholder} />
            </div>
            <div>
              <label className='text-sm font-medium'>{legalText.dialog.sortOrderLabel}</label>
              <Input type='number' value={state.sortOrder} onChange={e=>setState(s=>({...s, sortOrder: parseInt(e.target.value||'0',10)}))} />
            </div>
            <div>
              <label className='text-sm font-medium'>{legalText.dialog.activeLabel}</label>
              <Switch checked={state.isActive} onCheckedChange={val=>setState(s=>({...s, isActive: val}))} />
            </div>
            <div>
              <label className='text-sm font-medium'>{legalText.dialog.languageLabel}</label>
              <select className='border rounded px-2 py-1 ml-2' value={state.lang} onChange={e=>setState(s=>({...s, lang: e.target.value as SupportedLanguage}))}>
                {SUPPORTED_LANGUAGES.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className='text-sm font-medium'>{legalText.dialog.contentLabel.replace('{lang}', state.lang)}</label>
              <Textarea rows={12} value={state.content[state.lang] || ''} onChange={e=>setState(s=>({...s, content: {...s.content, [s.lang]: e.target.value}}))} placeholder={`<h1>${state.title}</h1>`} />
            </div>
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={close}>{legalText.buttons.cancel}</Button>
            <Button onClick={save} disabled={createMutation.isPending || updateMutation.isPending}>
              {(createMutation.isPending||updateMutation.isPending)? legalText.buttons.saving : legalText.buttons.save}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default LegalPagesManager;
